import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  buildStationTimingEmptySelection,
  IStationTimingPreviewSelection,
  STATION_TIMING_PREVIEW_MODAL_WIDTH,
  StationTimingPreview,
} from "@ryde/controls";
import { useCourseDtoActions, useModalConfirm } from "@ryde/redux";
import {
  collapseOptionalRouteLegsAcrossWaypoints,
  ConfirmResult,
  courseHasUnsavedStationChangeAtom,
  errors,
  expandCollapsedRouteLegsAcrossWaypoints,
  filterRealRouteStations,
  getCoursePlannedLegSeconds,
  ICourseDto,
  Log,
  MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION,
  STATION_TIMING_SOURCE_COURSE,
  StationTimingApi,
  updateCourseMetadataStationsTimeBulk,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const useCourseMeasuredTimes = (
  courseDto: Partial<ICourseDto> | undefined,
) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const { updateEditingCourseMetadata } = useCourseDtoActions();
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );
  const [isLoading, setIsLoading] = useState(false);
  // `isLoading` state only reaches this closure on the next render, so a second click inside the same
  // tick would fire a second request; the ref is what actually keeps the flow single-flight.
  const isRequestInFlightRef = useRef(false);
  const selectionRef = useRef<IStationTimingPreviewSelection>(
    buildStationTimingEmptySelection(),
  );

  const handleSelectionChange = useEvent(
    (selection: IStationTimingPreviewSelection): void => {
      selectionRef.current = selection;
    },
  );

  const openMeasuredTimes = useEvent(async (): Promise<void> => {
    const stations = courseDto?.metadata?.stationsInfo || [];
    const realStations = filterRealRouteStations(stations);

    if (
      !courseDto ||
      realStations.length < MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION
    ) {
      Log.warnAndToast(t(errors.stationTiming.notEnoughStations));
      return;
    }

    if (isRequestInFlightRef.current) {
      return;
    }

    isRequestInFlightRef.current = true;
    setIsLoading(true);
    // A course pools every day of the week into one number, so it asks for the per-day breakdown that
    // shows whether Friday traffic is really the Monday traffic the single number implies.
    const statsResult = await StationTimingApi.getStationTimingPairStats({
      sourceEntity: STATION_TIMING_SOURCE_COURSE,
      stationIds: realStations.map(station => station.id),
      shouldIncludeDayOfWeek: true,
    });
    setIsLoading(false);
    isRequestInFlightRef.current = false;

    if (!statsResult.success) {
      warnAndToastSimpleResult(statsResult, t);
      return;
    }

    const stats = statsResult.data;

    if (!stats) {
      Log.warnAndToast(t(errors.stationTiming.statsNotLoaded));
      return;
    }

    selectionRef.current = buildStationTimingEmptySelection();

    const confirmationResult = await confirmDialogResult({
      title: t("stationTiming.MEASURED_TIMES_TITLE"),
      okButtonTitle: t("stationTiming.MEASURED_TIMES_APPLY"),
      width: STATION_TIMING_PREVIEW_MODAL_WIDTH,
      customContent: (
        <StationTimingPreview
          stations={realStations.map(station => ({
            id: station.id,
            name: station.name,
          }))}
          plannedLegSeconds={
            collapseOptionalRouteLegsAcrossWaypoints({
              stations,
              legs: getCoursePlannedLegSeconds(courseDto.metadata),
            }) ?? []
          }
          stats={stats}
          onChange={handleSelectionChange}
        />
      ),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const selection = selectionRef.current;

    if (!selection.selectedLegIndexes.length) {
      Log.warnAndToast(t(errors.stationTiming.noMeasurementSelected));
      return;
    }

    // Applied to the edited form only: skipApiCheck keeps a route recount from overwriting the
    // measured times, and the unsaved flag keeps Save enabled until the user persists the course.
    const metadata = updateCourseMetadataStationsTimeBulk({
      courseDto,
      legSeconds:
        expandCollapsedRouteLegsAcrossWaypoints({
          stations,
          collapsedLegs: selection.legSeconds,
        }) ?? selection.legSeconds,
    });
    const result = await updateEditingCourseMetadata({
      metadata,
      optimize: false,
      skipApiCheck: true,
    });

    if (!result.success) {
      warnAndToastSimpleResult(result, t);
      return;
    }

    setCourseHasUnsavedStationChange(true);
    Log.infoAndToast(t("stationTiming.MEASURED_TIMES_APPLIED"));
  });

  return {
    isLoading,
    openMeasuredTimes,
  };
};
